import React, { useEffect, useMemo, useRef } from 'react';
import MenuItem from '@components/catalog/catalog-hotfilters/catalog-category-hotfilter/menu-item/menu-item';

import { ElasticSearchFacet } from '@interfaces/api/responses/elasticSearch';
import useMaterialFilterFacets from '@hooks/filters/use-material-filter-facets';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import { CatalogMaterialHotfilterContext } from '@context/catalog-material-hotfilter.context';
import { facetToFilterRename } from '@maps/catalog/search-product-facets-filters-maps';
import { CatalogContext } from '@context/catalog.context';
import { ElasticSearch, ElasticSearchStringListFilterProperties } from '@interfaces/models/elasticSearch';
import { useFilterListKeyboardNavigation } from '@hooks/a11y/use-filter-list-keyboard-navigation';

interface MaterialMenuItemProps {
  materialFacet: ElasticSearchFacet;
  activeFilter: ElasticSearchStringListFilterProperties;
}

const ALL_ITEMS_ID = 'catalog-material-hotfilter-all-items';

const MaterialMenuItem: React.FC<MaterialMenuItemProps> = ({ materialFacet, activeFilter }) => {
  const menuState = useContextSelector(CatalogMaterialHotfilterContext, (context) => context.menuState);
  const setMenuState = useContextSelector(CatalogMaterialHotfilterContext, (context) => context.setMenuState);
  const childLevel = useContextSelector(CatalogMaterialHotfilterContext, (context) => context.childLevel);
  const removeMaterialHotfilter = useContextSelector(
    CatalogMaterialHotfilterContext,
    (context) => context.removeMaterialHotfilter,
  );
  const applyMaterialHotFilter = useContextSelector(
    CatalogMaterialHotfilterContext,
    (context) => context.applyMaterialHotFilter,
  );
  const filters = useContextSelector(CatalogContext, (context) => context.filters);
  const staticFilterGroups = useContextSelector(CatalogContext, (context) => context.staticFilterGroups);
  const filterGroup = menuState.activeLevel;
  const isSelected = filters[activeFilter]?.includes(materialFacet.id);

  const childFacets = useMaterialFilterFacets({
    filterGroup: childLevel,
    parentId: materialFacet.id,
    filterGroupSortBy: 'asc',
  });

  const hasChildren = childFacets.length > 0;
  const childFilterGroup = childLevel
    ? (facetToFilterRename(childLevel) as ElasticSearchStringListFilterProperties)
    : undefined;
  const isHighlighted = useMemo(
    () => childFacets.some((facet) => filters[childFilterGroup]?.includes(facet.id)),
    [childFacets, childFilterGroup, filters],
  );

  const handleClick = () => {
    if (!hasChildren) {
      if (isSelected) {
        removeMaterialHotfilter({
          id: materialFacet.id,
          facet: filterGroup,
        });
        return;
      }
      applyMaterialHotFilter({
        id: materialFacet.id,
        facet: filterGroup,
      });
      return;
    }
    setMenuState((prev) => ({
      ...prev,
      activeLevel: childLevel,
      [filterGroup]: {
        id: materialFacet.id,
        name: materialFacet.name,
      },
    }));
    // focus the first item in the list when navigating to a child material
    // for keyboard navigation accessibility
    window.requestAnimationFrame(() => {
      document?.getElementById(ALL_ITEMS_ID)?.focus();
    });
  };

  return (
    <MenuItem
      key={`material-menu-item-${materialFacet.id}`}
      label={materialFacet.name}
      handleClick={handleClick}
      hasChildren={hasChildren}
      isSelected={isSelected}
      isHighlighted={isHighlighted}
      isDisabled={!!staticFilterGroups[filterGroup]}
      dataCySelector={`${filterGroup}-${materialFacet.id}-${hasChildren ? `arrow` : `filter`}`}
    />
  );
};

const MaterialMenuList: React.FC = () => {
  const menuState = useContextSelector(CatalogMaterialHotfilterContext, (context) => context.menuState);
  const parentLevel = useContextSelector(CatalogMaterialHotfilterContext, (context) => context.parentLevel);
  const applyMaterialHotFilter = useContextSelector(
    CatalogMaterialHotfilterContext,
    (context) => context.applyMaterialHotFilter,
  );
  const removeMaterialHotfilter = useContextSelector(
    CatalogMaterialHotfilterContext,
    (context) => context.removeMaterialHotfilter,
  );

  const filters = useContextSelector(CatalogContext, (context) => context.filters);
  const setFilters = useContextSelector(CatalogContext, (context) => context.setFilters);
  const staticFilterGroups = useContextSelector(CatalogContext, (context) => context.staticFilterGroups);
  const isManualSwitchRef = useContextSelector(CatalogContext, (context) => context.isManualSwitchRef);

  const filterGroup = menuState.activeLevel;
  const parentId = parentLevel ? menuState[parentLevel]?.id : undefined;
  const filterGroupSortBy = 'asc';
  const hasAllItems = ['materialLvl1'].includes(menuState.activeLevel);
  const facets = useMaterialFilterFacets({ filterGroup, parentId, filterGroupSortBy });

  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);

  const { parentFilter, activeFilter } = useMemo(() => {
    return {
      parentFilter: facetToFilterRename(parentLevel) as ElasticSearchStringListFilterProperties,
      activeFilter: facetToFilterRename(menuState.activeLevel) as ElasticSearchStringListFilterProperties,
    };
  }, [menuState, parentLevel]);

  const isAllItemsSelected = useMemo(() => {
    if (!parentId || !filters[parentFilter]?.includes(menuState[parentLevel]?.id)) {
      return false;
    }

    // Check if there are any materialLvl1 children of this specific parent selected
    const currentMaterialLvl1Filters = filters[activeFilter] || [];

    // Get all possible children IDs for this parent
    const childrenOfThisParent = facets.map((facet) => facet.id);

    // Check if any of the selected materialLvl1 belong to this parent
    const hasChildrenSelected = currentMaterialLvl1Filters.some((filterId) => childrenOfThisParent.includes(filterId));

    return !hasChildrenSelected;
  }, [activeFilter, filters, menuState, parentFilter, parentLevel, parentId, facets]);

  const handleAllItemsClick = () => {
    if (isAllItemsSelected) {
      removeMaterialHotfilter({
        id: parentId,
        facet: parentLevel,
      });
      return;
    }

    // When selecting "All Items", add the parent and remove all its children
    const currentMaterialLvl1Filters = filters['materialLvl1.id'] || [];
    const childrenOfThisParent = facets.map((facet) => facet.id);

    // Remove all materialLvl1 children of this parent
    const newMaterialLvl1Filters = currentMaterialLvl1Filters.filter(
      (filterId) => !childrenOfThisParent.includes(filterId),
    );

    applyMaterialHotFilter({
      id: parentId,
      facet: parentLevel,
    });

    // If we removed some materialLvl1 filters, update the state
    if (newMaterialLvl1Filters.length !== currentMaterialLvl1Filters.length) {
      isManualSwitchRef.current = true;
      const newState: ElasticSearch['filters'] = {
        ...filters,
        'materialLvl0.id': [...new Set([...(filters['materialLvl0.id'] || []), parentId])],
        ...(newMaterialLvl1Filters.length > 0 && { 'materialLvl1.id': newMaterialLvl1Filters }),
      };

      if (newMaterialLvl1Filters.length === 0) {
        delete newState['materialLvl1.id'];
      }

      setFilters(newState);
    }
  };

  const {
    handleKeyDown: handleListKeyDown,
    collectItems,
    resetFocusIndex,
  } = useFilterListKeyboardNavigation({
    listRef,
    disabled: !!staticFilterGroups[filterGroup],
  });

  // Re-collect items and reset focus when the active level or facets change (hierarchy drill-down)
  useEffect(() => {
    collectItems();
    resetFocusIndex(0);
  }, [menuState.activeLevel, facets, collectItems, resetFocusIndex]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onKeyDown delegates keyboard events bubbling from interactive children for roving tabindex navigation
    <ul
      ref={listRef}
      role="group"
      aria-label={t('CATALOG.FILTERS.MATERIAL')}
      onKeyDown={handleListKeyDown}
      data-cy={`${filterGroup}-menu-list`}
    >
      {hasAllItems && (
        <li data-cy={`${filterGroup}-menu-list-all-items`}>
          <MenuItem
            handleClick={handleAllItemsClick}
            hasChildren={false}
            isSelected={isAllItemsSelected}
            label={t('FAVORITE_PAGE.FILTER.SUB_CATEGORIES_ALL_ITEMS')}
            isDisabled={!!staticFilterGroups[parentLevel]}
            dataCySelector={`${parentLevel}-${parentId}-filter`}
            htmlId={ALL_ITEMS_ID}
          />
        </li>
      )}
      {facets.map((facet) => (
        <li
          data-cy={`${filterGroup}-menu-list-item-${facet.id}`}
          key={`${filterGroup}-menu-item-${facet.id}`}
        >
          <MaterialMenuItem
            materialFacet={facet}
            activeFilter={activeFilter}
          />
        </li>
      ))}
    </ul>
  );
};

export default MaterialMenuList;
